import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Grid, 
  Paper, 
  TextField, 
  Typography,
  InputAdornment,
  Fab,
  Tooltip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  Chip,
  Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuidv4 } from 'uuid';
import { Dimensions, Product, BRAND_COLORS, FONTS, MeasurementUnit, RotationOption } from '../types/index.ts';
import { getPreferredUnit, setPreferredUnit } from '../services/preferences.ts';

interface ProductInputFormProps {
  onProductSubmit: (products: Product[]) => void;
  isLoading?: boolean;
  // Signale un changement de préférence (unité) pour que l'app (status rail)
  // se rafraîchisse immédiatement.
  onPreferencesChanged?: () => void;
}

// L'état du formulaire garde les CHAÎNES saisies : parser à chaque frappe
// (ancien comportement) avalait le « 0 » de tête — taper « 0.75 » était
// impossible. Tout est parsé/validé à la soumission uniquement.
interface ProductFormState extends Omit<Product, 'quantity' | 'dimensions'> {
  quantity: string | number;
  dimensions: { length: string; width: string; height: string };
}

// Brouillon persistant : ne pas obliger l'utilisateur à tout retaper à chaque
// visite. Stocké dans localStorage, validé au chargement (jamais de crash sur
// des données inattendues).
const PRODUCT_DRAFT_KEY = 'killian_product_draft';

const toDimensionString = (value: unknown): string => {
  if (typeof value === 'string') return value;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? String(n) : '';
};

const loadDraft = (): ProductFormState[] | null => {
  try {
    const raw = localStorage.getItem(PRODUCT_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed.slice(0, 20).map((item: any) => ({
      id: typeof item?.id === 'string' && item.id ? item.id : uuidv4(),
      quantity:
        typeof item?.quantity === 'number' || typeof item?.quantity === 'string'
          ? item.quantity
          : 1,
      dimensions: {
        length: toDimensionString(item?.dimensions?.length),
        width: toDimensionString(item?.dimensions?.width),
        height: toDimensionString(item?.dimensions?.height),
      },
      unit: item?.unit === 'cm' ? 'cm' : 'in',
      rotationOptions: Array.isArray(item?.rotationOptions)
        ? item.rotationOptions.filter((r: any) => ['length', 'width', 'flipped'].includes(r))
        : [],
    }));
  } catch {
    return null;
  }
};

const saveDraft = (products: ProductFormState[]): void => {
  try {
    localStorage.setItem(PRODUCT_DRAFT_KEY, JSON.stringify(products));
  } catch {
    /* stockage indisponible : le formulaire fonctionne sans persistance */
  }
};

const ProductInputForm: React.FC<ProductInputFormProps> = ({ 
  onProductSubmit,
  isLoading = false,
  onPreferencesChanged
}) => {
  // New products default to the user's remembered unit (in / cm).
  const makeEmptyProduct = (): ProductFormState => ({
    id: uuidv4(),
    quantity: 1,
    dimensions: { length: '', width: '', height: '' },
    unit: getPreferredUnit(),
    rotationOptions: []
  });

  const [products, setProducts] = useState<ProductFormState[]>(
    () => loadDraft() ?? [makeEmptyProduct()]
  );

  // Persister le brouillon à chaque modification.
  useEffect(() => {
    saveDraft(products);
  }, [products]);

  const handleAddProduct = () => {
    setProducts([...products, makeEmptyProduct()]);
  };

  const handleRemoveProduct = (productId: string) => {
    if (products.length > 1) {
      setProducts(products.filter(product => product.id !== productId));
    }
  };

  // Stocke la saisie telle quelle (chaîne) : le parsing n'a lieu qu'à la
  // validation, sinon taper « 0.75 » est impossible (le 0 était avalé).
  const handleDimensionChange = (
    productId: string,
    dimension: keyof Dimensions,
    value: string
  ) => {
    setProducts(products.map(product =>
      product.id === productId
        ? {
            ...product,
            dimensions: {
              ...product.dimensions,
              [dimension]: value
            }
          }
        : product
    ));
  };

  // Accepte la virgule décimale ; NaN pour tout ce qui est vide ou ambigu.
  const parseDimension = (raw: string): number => {
    const normalized = raw.replace(',', '.').trim();
    if (normalized === '') return NaN;
    return Number(normalized);
  };

  // Number() (et non parseInt) : « 1e3 » vaut 1000, pas 1 ; « 2.5 » est
  // rejeté comme quantité au lieu d'être tronqué en silence.
  const parseQuantity = (raw: string | number): number => Number(raw.toString().trim());

  const handleQuantityChange = (productId: string, value: string) => {
    // Allow empty input for better user experience during typing
    setProducts(products.map(product => 
      product.id === productId
        ? { ...product, quantity: value }
        : product
    ));
  };

  const handleUnitChange = (productId: string, unit: MeasurementUnit) => {
    // Remember the choice so it persists across navigation / reloads.
    setPreferredUnit(unit);
    onPreferencesChanged?.();
    setProducts(products.map(product => {
      if (product.id === productId) {
        // Convert dimensions when changing units (factors are exact inverses).
        // Les champs vides ou non numériques restent tels quels (pas de « 0 »).
        const conversionFactor = unit === 'cm' && product.unit === 'in' ? 2.54 :
                                 unit === 'in' && product.unit === 'cm' ? 1 / 2.54 : 1;
        const convertField = (raw: string): string => {
          if (conversionFactor === 1) return raw;
          const value = parseDimension(raw);
          if (!Number.isFinite(value)) return raw;
          return String(parseFloat((value * conversionFactor).toFixed(3)));
        };

        return {
          ...product,
          unit,
          dimensions: {
            length: convertField(product.dimensions.length),
            width: convertField(product.dimensions.width),
            height: convertField(product.dimensions.height)
          }
        };
      }
      return product;
    }));
  };

  const handleRotationOptionsChange = (
    productId: string, 
    newOptions: RotationOption[]
  ) => {
    setProducts(products.map(product => 
      product.id === productId
        ? { ...product, rotationOptions: newOptions }
        : product
    ));
  };

  // A product is valid only with a whole quantity >= 1 and all dimensions > 0.
  const isQuantityValid = (p: ProductFormState): boolean => {
    const quantity = parseQuantity(p.quantity);
    return Number.isInteger(quantity) && quantity >= 1;
  };

  const isDimensionValid = (raw: string): boolean => parseDimension(raw) > 0;

  const isProductValid = (p: ProductFormState): boolean =>
    isQuantityValid(p) &&
    isDimensionValid(p.dimensions.length) &&
    isDimensionValid(p.dimensions.width) &&
    isDimensionValid(p.dimensions.height);

  const isFormValid = products.every(isProductValid);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    // Block submission of invalid input (empty / 0 / negative dimensions).
    if (!isFormValid) return;

    const validatedProducts: Product[] = products.map(product => ({
      id: product.id,
      quantity: parseQuantity(product.quantity),
      dimensions: {
        length: parseDimension(product.dimensions.length),
        width: parseDimension(product.dimensions.width),
        height: parseDimension(product.dimensions.height),
      },
      unit: product.unit,
      rotationOptions: product.rotationOptions,
    }));

    onProductSubmit(validatedProducts);
  };

  // Rotation options for selection
  const rotationOptions = [
    { value: 'length', label: 'Rotate on length' },
    { value: 'width', label: 'Rotate on width' },
    { value: 'flipped', label: 'Flipped' }
  ];

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        width: '100%',
        mb: 4,
        animation: 'fadeIn 0.5s ease-in-out',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(20px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' }
        }
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ color: BRAND_COLORS.primary, fontWeight: 'bold' }}>
        Product Information
      </Typography>
      
      <Typography variant="body1" paragraph>
        Enter the dimensions of each product and the total quantity.
        Select rotation options to optimize packaging.
      </Typography>

      {products.map((product, index) => (
        <Paper
          key={product.id}
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            position: 'relative',
            transition: 'all 0.3s ease',
            backgroundColor: BRAND_COLORS.cardStock,
            border: `1.5px solid ${BRAND_COLORS.ink}`,
            ':hover': {
              borderColor: BRAND_COLORS.dockBlue
            }
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ color: BRAND_COLORS.primary }}>
            Product {index + 1}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Quantity"
                type="number"
                fullWidth
                value={product.quantity}
                onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                InputProps={{
                  inputProps: { min: 1, step: 1 }
                }}
                sx={{ '& input': { fontFamily: FONTS.mono } }}
                required
                variant="outlined"
                error={!isQuantityValid(product)}
                helperText={!isQuantityValid(product) ? 'Enter a whole number of at least 1' : ''}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Length"
                type="number"
                fullWidth
                value={product.dimensions.length}
                onChange={(e) => handleDimensionChange(product.id, 'length', e.target.value)}
                InputProps={{
                  inputProps: { min: 0, step: 0.001 },
                  endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontFamily: FONTS.mono } }}>{product.unit}</InputAdornment>,
                }}
                sx={{ '& input': { fontFamily: FONTS.mono } }}
                required
                variant="outlined"
                error={product.dimensions.length !== '' && !isDimensionValid(product.dimensions.length)}
                helperText={product.dimensions.length !== '' && !isDimensionValid(product.dimensions.length) ? 'Must be greater than 0' : ''}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Width"
                type="number"
                fullWidth
                value={product.dimensions.width}
                onChange={(e) => handleDimensionChange(product.id, 'width', e.target.value)}
                InputProps={{
                  inputProps: { min: 0, step: 0.001 },
                  endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontFamily: FONTS.mono } }}>{product.unit}</InputAdornment>,
                }}
                sx={{ '& input': { fontFamily: FONTS.mono } }}
                required
                variant="outlined"
                error={product.dimensions.width !== '' && !isDimensionValid(product.dimensions.width)}
                helperText={product.dimensions.width !== '' && !isDimensionValid(product.dimensions.width) ? 'Must be greater than 0' : ''}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Height"
                type="number"
                fullWidth
                value={product.dimensions.height}
                onChange={(e) => handleDimensionChange(product.id, 'height', e.target.value)}
                InputProps={{
                  inputProps: { min: 0, step: 0.001 },
                  endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontFamily: FONTS.mono } }}>{product.unit}</InputAdornment>,
                }}
                sx={{ '& input': { fontFamily: FONTS.mono } }}
                required
                variant="outlined"
                error={product.dimensions.height !== '' && !isDimensionValid(product.dimensions.height)}
                helperText={product.dimensions.height !== '' && !isDimensionValid(product.dimensions.height) ? 'Must be greater than 0' : ''}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <Typography
                  variant="body2"
                  gutterBottom
                  sx={{ fontFamily: FONTS.display, textTransform: 'uppercase', letterSpacing: '0.06em' }}
                >
                  Unit
                </Typography>
                <ToggleButtonGroup
                  value={product.unit}
                  exclusive
                  onChange={(_, unit) => unit && handleUnitChange(product.id, unit as MeasurementUnit)}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  <ToggleButton value="in" sx={{ px: 3 }}>
                    in
                  </ToggleButton>
                  <ToggleButton value="cm" sx={{ px: 3 }}>
                    cm
                  </ToggleButton>
                </ToggleButtonGroup>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <Autocomplete
                  multiple
                  id={`rotation-options-${product.id}`}
                  options={rotationOptions}
                  getOptionLabel={(option) => option.label}
                  value={rotationOptions.filter(option => 
                    product.rotationOptions.includes(option.value as RotationOption)
                  )}
                  onChange={(_, newValues) => {
                    const selectedOptions = newValues.map(item => item.value) as RotationOption[];
                    handleRotationOptionsChange(product.id, selectedOptions);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Rotation Options"
                      helperText="Allowing more rotations lets the calculator fit more products per box"
                    />
                  )}
                  renderTags={(selectedItems, getTagProps) =>
                    selectedItems.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.label}
                        {...getTagProps({ index })}
                        sx={{ 
                          bgcolor: `${BRAND_COLORS.secondary}22`, 
                          borderColor: BRAND_COLORS.secondary 
                        }}
                      />
                    ))
                  }
                />
              </FormControl>
            </Grid>
          </Grid>

          {products.length > 1 && (
            <Tooltip title="Remove this product">
              <Fab
                size="small"
                color="error"
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                }}
                onClick={() => handleRemoveProduct(product.id)}
              >
                <DeleteIcon />
              </Fab>
            </Tooltip>
          )}
        </Paper>
      ))}

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddProduct}
          sx={{
            borderColor: BRAND_COLORS.primary,
            color: BRAND_COLORS.primary,
            '&:hover': {
              borderColor: BRAND_COLORS.secondary,
              backgroundColor: `${BRAND_COLORS.secondary}10`,
            }
          }}
        >
          Add Another Product
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          type="submit"
          variant="contained"
          disabled={isLoading || !isFormValid}
          sx={{
            backgroundColor: BRAND_COLORS.secondary,
            color: 'white',
            py: 1.5,
            px: 4,
            '&:hover': {
              backgroundColor: '#0C7038',
            }
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} sx={{ color: 'white' }} />
          ) : (
            'Find Optimal Packaging Solutions'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ProductInputForm; 